import { supabase } from './supabaseClient';

export async function adminSession() {
  const { data: userData, error: userError } = await supabase.auth.getUser();
  if (userError || !userData?.user) return null;
  const { data, error } = await supabase.rpc('admin_session');
  if (error) throw new Error(error.message);
  if (!data || !['admin', 'company'].includes(data.role) || data.userId !== userData.user.id) {
    throw new Error('This account has no active workspace access. Contact your administrator.');
  }
  return data;
}

export async function signInAdmin(email, password) {
  const { error } = await supabase.auth.signInWithPassword({ email: email.trim(), password });
  if (error) throw new Error('Unable to sign in. Check your email and password.');
  try {
    const session = await adminSession();
    if (!session) throw new Error('Please sign in again.');
    if (!session.passwordChangeRequired) await syncFileSession();
    return session;
  } catch (error) {
    await supabase.auth.signOut();
    throw error;
  }
}

// The parameters remain for compatibility with installed feature RPC signatures.
// Authorization now comes exclusively from Supabase's signed Authorization header.
export function adminCompanyArgs(company) {
  if (!company?.id) throw new Error('Select a company first.');
  return { p_company_id: company.id, p_username: '', p_password: '' };
}

export async function adminFileAuth(company) {
  const { data } = await supabase.auth.getSession();
  if (!data?.session?.access_token) throw new Error('Please sign in again.');
  return { ...adminCompanyArgs(company), access_token: data.session.access_token };
}

export async function syncFileSession(employeeSession = null) {
  const { data } = employeeSession ? { data: null } : await supabase.auth.getSession();
  const response = await fetch('/api/session', {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(employeeSession ? {
      employee_id: employeeSession.employeeId, token: employeeSession.employeeSessionToken
    } : { access_token: data?.session?.access_token })
  });
  if (!response.ok) throw new Error('Could not establish secure file access. Please sign in again.');
}

export async function signOutAdmin() {
  await fetch('/api/session', { method: 'DELETE' }).catch(() => {});
  await supabase.auth.signOut();
}

export async function provisionCompanyAdmin(companyId, email, password) {
  const { data } = await supabase.auth.getSession();
  const response = await fetch('/api/admin-users', {
    method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${data?.session?.access_token || ''}` },
    body: JSON.stringify({ company_id: companyId, email: email.trim(), password })
  });
  const result = await response.json();
  if (!response.ok) throw new Error(result.error || 'Could not create administrator access.');
  return result;
}
